import { Injectable } from '@angular/core';
import { MessageType } from '@epicuro-next/ataraxia/snack-bar';
import { AccountsService } from '@epicuro-next/data/accounts';
import { BasketReviewService } from '@epicuro-next/data/baskets';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import { selectAccountReviewersV2Enabled } from '@epicuro-next/platform/configuration';
import { registerSentryUserAccount } from '@epicuro-next/platform/sentry';
import { go } from '@epicuro-next/state/router-store';
import { showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Account } from 'graphql-api/gql-api-interfaces';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  clearActiveAccount,
  confirmActiveAccountSelection,
  loadAccountCountries,
  loadAccountCountriesFailure,
  loadAccountCountriesSuccess,
  loadAccountDetailsFailure,
  loadAccountDetailsSuccess,
  loadAccountReviewers,
  loadAccountReviewersFailure,
  loadAccountReviewersSuccess,
  loadAccounts,
  loadAccountsFailure,
  loadAccountsSuccess,
  setActiveAccount,
  setPaginationQuery,
  setSearchQuery,
} from '../actions/accounts.actions';
import {
  selectAccounts,
  selectAccountsQueryForRequest,
  selectActiveAccount,
  selectActiveAccountId,
  selectIsBeSearchEnabled,
  selectIsBESearchEnabledByRole,
} from '../selectors/accounts.selectors';
import { getFilteredAccounts } from '../shared/get-filtered-accounts';

@Injectable()
export class AccountsEffects {
  public loadAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccounts),
      debounceTime(300),
      // wait user role to select search query page size
      switchMap(() =>
        this.store.pipe(
          select(selectIsBESearchEnabledByRole),
          filter(isNonNullish),
          take(1),
        ),
      ),
      concatLatestFrom(() =>
        this.store.pipe(select(selectAccountsQueryForRequest)),
      ),
      switchMap(([_, query]) => {
        return this.accountService.getAccounts(query).pipe(
          map(({ list, total }) =>
            loadAccountsSuccess({ list, total, pageSize: query.size }),
          ),
          catchError((error) =>
            of(loadAccountsFailure({ error: error.message })),
          ),
        );
      }),
    ),
  );

  public loadAccountsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountsFailure),
      map(() =>
        showSnackBarMessage({
          message: $localize`:accounts|Failed get accounts message@@failedGetAccounts:Failed to get accounts`,
          hideDelay: 10000,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  public confirmSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmActiveAccountSelection),
      concatLatestFrom(() => this.store.pipe(select(selectActiveAccountId))),
      filter(
        ([{ activeAccount }, currentActiveAccountId]) =>
          activeAccount?.accountId !== currentActiveAccountId,
      ),
      map(([{ activeAccount }]) =>
        setActiveAccount({ account: activeAccount }),
      ),
    ),
  );

  public getAccountDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      concatLatestFrom(() => this.store.pipe(select(selectActiveAccount))),
      filter(([{ isDetailedAccount }]) => !isDetailedAccount),
      map(([{ account }]) => account),
      distinctUntilKeyChanged<Account>('accountId'),
      switchMap((account) => {
        return this.accountService
          .getAccountDetails({ accountId: account.accountId })
          .pipe(
            map((accountDetail) =>
              loadAccountDetailsSuccess({ account: accountDetail }),
            ),
            catchError((err) => {
              return [
                loadAccountDetailsFailure({ error: err.message }),
                clearActiveAccount(),
                go({ commands: [APP_TOP_LEVEL_PATH.accountSelector.path] }),
              ];
            }),
          );
      }),
    ),
  );

  public registerSentryUserAccount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setActiveAccount),
        map(({ account }) => account),
        distinctUntilKeyChanged<Account>('accountId'),
        tap(({ accountId }) => registerSentryUserAccount(accountId)),
      ),
    { dispatch: false },
  );

  public resetAccountCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountDetailsSuccess, loadAccountDetailsFailure),
      map(() => loadAccountCountriesSuccess({ countries: [] })),
    ),
  );

  public getAccountCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountCountries),
      withLatestFrom(this.store.pipe(select(selectActiveAccountId))),
      switchMap(([, accountId]) => {
        if (!accountId) throw new Error('Failed to load account countries');

        return this.accountService
          .getAccountCountries(accountId)
          .pipe(map((countries) => loadAccountCountriesSuccess({ countries })));
      }),
      catchError((err) =>
        of(loadAccountCountriesFailure({ message: err.message })),
      ),
    ),
  );

  private readonly setAccountsQuery$ = this.actions$.pipe(
    ofType(setSearchQuery),
    filter(({ query }) => Boolean(query)),
  );

  public loadAccountsOnQueryChange$ = createEffect(() =>
    this.setAccountsQuery$.pipe(
      concatLatestFrom(() => this.store.pipe(select(selectIsBeSearchEnabled))),
      filter(([_, searchEnabled]) => searchEnabled),
      map(() => loadAccounts()),
    ),
  );

  public filterLoadedAccountOnQueryChange$ = createEffect(() =>
    this.setAccountsQuery$.pipe(
      concatLatestFrom(() => this.store.pipe(select(selectIsBeSearchEnabled))),
      filter(([_, searchEnabled]) => !searchEnabled),
      concatLatestFrom(() => this.store.select(selectAccounts)),
      map(([[{ query }], accounts]) => getFilteredAccounts(accounts, query)),
      map((filteredAccounts) => {
        return loadAccountsSuccess({
          list: filteredAccounts,
          total: filteredAccounts.length,
          // for correctly setting pagination (allSearchAccountPagesLoaded)
          pageSize: filteredAccounts.length + 1,
        });
      }),
    ),
  );

  public setPaginationQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPaginationQuery),
      map(() => loadAccounts()),
    ),
  );

  public getAccountReviewers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountReviewers),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
        this.store.pipe(select(selectAccountReviewersV2Enabled)),
      ),
      switchMap(([{ basketId }, accountId, isV2ApiEnabled]) => {
        const api$ = isV2ApiEnabled
          ? this.basketReviewerService.getAccountBasketReviewers(basketId)
          : this.accountService.getAccountReviewers(accountId);
        return api$.pipe(
          map((reviewers) => loadAccountReviewersSuccess({ reviewers })),
          catchError((err) =>
            of(loadAccountReviewersFailure({ message: err.message })),
          ),
        );
      }),
    ),
  );

  public loadAccountReviewersFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountReviewersFailure),
      map(() =>
        showSnackBarMessage({
          message: $localize`:accounts|Failed get prefered sales rep message@@failedGetFavoriteSalesReps:Failed to get favorite sales representatives`,
          hideDelay: 10000,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private accountService: AccountsService,
    private readonly basketReviewerService: BasketReviewService,
    private store: Store,
  ) {}
}
